import { ArrowClockwise, Trash, UploadSimple } from "@phosphor-icons/react";
import { clsx } from "clsx";

import UploadAttachment from "common/components/UploadAttachment";
import { Button } from "common/components/ui/Button";
import Label from "common/components/ui/Label";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAgencySettings from "../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../datahooks/useAgencySettingsMutations";
import SettingsItemContainer from "../SettingsItemContainer";

function AttachmentBox({
  className,
  label,
  onUpload,
  onDelete,
  imageUrl,
  recommendationText,
}: {
  className: string;
  label: string;
  onUpload: () => void;
  onDelete: () => void;
  imageUrl: string | undefined;
  recommendationText: string;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const boxClassName = clsx(
    "group p-3 flex h-40 rounded-2xl items-center justify-center border border-black-200 hover:bg-black-50",
    imageUrl
      ? "bg-white border-solid hover:border-dashed"
      : "bg-whiteGray border-dashed",
    className,
  );

  const actionButtons = (
    <div className="flex gap-x-2">
      <Button
        variant="secondary-black"
        size="sm"
        intent={imageUrl ? "iconOnly" : "labelIcon"}
        onClick={onUpload}
      >
        {imageUrl ? <ArrowClockwise /> : <UploadSimple />}
      </Button>
      {imageUrl && (
        <Button
          variant="outline-danger"
          size="sm"
          intent="iconOnly"
          onClick={onDelete}
        >
          <Trash />
        </Button>
      )}
    </div>
  );

  const content = (
    <>
      <div
        className={clsx(
          "flex-col items-center",
          imageUrl ? "hidden group-hover:flex" : "flex",
        )}
      >
        {actionButtons}
        <span className="mb-0.5 text-xs-bold mt-2">
          {imageUrl ? "Reupload or delete" : "Click to upload"}
        </span>
        <span className="text-black-500 text-button-xxs text-center">
          {recommendationText}
        </span>
      </div>
      {imageUrl && (
        <img src={imageUrl} alt="" className="group-hover:hidden max-h-[90%]" />
      )}
    </>
  );

  return (
    <div className={clsx("flex flex-col gap-y-1", className)}>
      <div className="justify-between flex items-center">
        <Label size="sm">{label}</Label>
        {!isDesktop && !!imageUrl && actionButtons}
      </div>
      {imageUrl ? (
        <div className={boxClassName}>{content}</div>
      ) : (
        <button type="button" className={boxClassName} onClick={onUpload}>
          {content}
        </button>
      )}
    </div>
  );
}

export default function Logo() {
  const { agencySettings } = useAgencySettings();
  const { removeAgencyLogoOrFavicon, updateAgencySettings } =
    useAgencySettingsMutations();

  const commonUploadProps = {
    maxFiles: 1,
    maxFileSize: 2 * 1024 * 1024,
    currentFileCount: 0,
    allowedMimeTypes: ["image/png"],
  };

  return (
    <SettingsItemContainer className="flex flex-col gap-y-3">
      <span className="text-button-sm">Agency logo</span>
      <div className="flex gap-3 flex-col lg:flex-row">
        <div className="flex flex-col gap-y-1">
          <UploadAttachment
            onFileUpload={(file) => {
              updateAgencySettings({
                updates: { logo: file },
              });
            }}
            onFileUploadError={() => {}}
            renderButton={(onClick) => {
              return (
                <AttachmentBox
                  label="Full logo"
                  className="max-w-96"
                  onUpload={onClick}
                  onDelete={() =>
                    removeAgencyLogoOrFavicon({
                      type: "logo",
                    })
                  }
                  imageUrl={agencySettings?.logo}
                  recommendationText="PNG up to 2MB • Recommended 120x40 • Transparent background"
                />
              );
            }}
            currentFileCount={0}
            {...commonUploadProps}
          />
          <span className="text-black-500 text-xs">
            Displayed in dashboard header
          </span>
        </div>
        <div className="flex flex-col gap-y-1">
          <UploadAttachment
            onFileUpload={(file) => {
              updateAgencySettings({
                updates: { favicon: file },
              });
            }}
            onFileUploadError={() => {}}
            renderButton={(onClick) => {
              return (
                <AttachmentBox
                  label="Icon / Favicon"
                  className="w-48"
                  onUpload={onClick}
                  onDelete={() =>
                    removeAgencyLogoOrFavicon({
                      type: "favicon",
                    })
                  }
                  imageUrl={agencySettings?.favicon}
                  recommendationText="PNG up to 2MB • Recommended 80x80 • Transparent background"
                />
              );
            }}
            currentFileCount={0}
            {...commonUploadProps}
          />
          <span className="text-black-500 text-xs">
            Dashboard logo and browser favicon
          </span>
        </div>
      </div>
    </SettingsItemContainer>
  );
}
